import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useField, useFormikContext } from "formik";
import { AxiosGetMethod } from "../helpers/customAxios";
import {
  indexUserForSearch,
  matchUserWithQuery,
  fetchTransliteration,
  getCachedTransliteration,
  parseNameAndInitial as defaultParseNameAndInitial,
} from "../helpers/tamilPhonetics";

const VillageUserNameInput = ({
  name = "name",
  id = "name",
  placeholder = "Full name",
  innerRef,
  nextRef,
  initialRef,
  occupationRef,
  parseNameAndInitial,
  moiList = [],
  customAxiosGet,
  apiGet: propApiGet,
  className,
  ...props
}) => {
  const [field, , helpers] = useField(name);
  const { values, setFieldValue } = useFormikContext();

  const parseFn = parseNameAndInitial || defaultParseNameAndInitial;

  const [villageUsers, setVillageUsers] = useState([]);
  const [apiSuggestions, setApiSuggestions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [loading, setLoading] = useState(false);

  const wrapperRef = useRef(null);
  const dropdownRef = useRef(null);
  const cacheRef = useRef({});

  const apiGet = propApiGet || customAxiosGet || AxiosGetMethod;

  // Local storage cache helper
  const getCachedUsers = useCallback((cacheKey) => {
    try {
      if (cacheRef.current[cacheKey]) {
        return cacheRef.current[cacheKey];
      }
      const raw = localStorage.getItem("dwt_village_users_cache");
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed[cacheKey]) {
          cacheRef.current[cacheKey] = parsed[cacheKey];
          return parsed[cacheKey];
        }
      }
    } catch (e) {}
    return null;
  }, []);

  const saveCachedUsers = useCallback((cacheKey, users) => {
    try {
      cacheRef.current[cacheKey] = users;
      const raw = localStorage.getItem("dwt_village_users_cache");
      const parsed = raw ? JSON.parse(raw) : {};
      parsed[cacheKey] = users;
      localStorage.setItem("dwt_village_users_cache", JSON.stringify(parsed));
    } catch (e) {}
  }, []);

  // Fetch users whenever values.village or values.area changes (empty if no village selected or new unlisted village)
  useEffect(() => {
    const rawVillage = (values?.village || "").trim();
    const rawArea = (values?.area || "").trim();

    if (!rawVillage) {
      setVillageUsers([]);
      setShowDropdown(false);
      setHighlightedIndex(-1);
      return;
    }

    const normVillage = rawVillage.toLowerCase();
    const normArea = rawArea.toLowerCase();
    const cacheKey = normArea ? `${normVillage}__${normArea}` : normVillage;

    // 1. Immediately check cache for fast UI
    const cached = getCachedUsers(cacheKey);
    if (cached && cached.length > 0) {
      setVillageUsers(cached);
    } else if (normArea) {
      // Fast fallback: filter village-level cached users by area for instant UI
      const baseCached = getCachedUsers(normVillage);
      if (baseCached && baseCached.length > 0) {
        const areaFiltered = baseCached.filter((u) => {
          const a = (u.area || "").trim().toLowerCase();
          return a === normArea || a.includes(normArea) || normArea.includes(a);
        });
        if (areaFiltered.length > 0) {
          setVillageUsers(areaFiltered);
        }
      }
    }

    // 2. Debounce fetch from backend & merge with current session moiList
    const timer = setTimeout(async () => {
      setLoading(true);
      let remoteUsers = [];
      try {
        const queryParams = new URLSearchParams();
        queryParams.set("village", rawVillage);
        if (rawArea) {
          queryParams.set("area", rawArea);
        }
        const queryString = queryParams.toString();
        const res = await apiGet(`entry/users-by-village?${queryString}`);
        if (res?.status === 200 && Array.isArray(res?.data?.data)) {
          remoteUsers = res.data.data;
        }
      } catch (err) {
        console.warn("Failed to fetch users by village/area:", err);
      } finally {
        setLoading(false);
      }

      // Collect from local moiList strictly for this village
      const localUsers = (moiList || [])
        .filter((item) => {
          if (!item.name || item.name.trim() === "") return false;
          const v = (item.village || "").trim().toLowerCase();
          if (v !== normVillage) return false;
          if (normArea) {
            const a = (item.area || "").trim().toLowerCase();
            if (a !== normArea && !a.includes(normArea) && !normArea.includes(a)) {
              return false;
            }
          }
          return true;
        })
        .map((item) => ({
          name: item.name,
          family_name: item.family_name || item.father_name || "",
          mobile: item.mobile || "",
          village: item.village || "",
          area: item.area || "",
          city: item.city || "",
          state: item.state || "",
          occupation: item.occupation || "",
        }));

      // Combine & Deduplicate by name + mobile + family_name
      const merged = [...remoteUsers, ...localUsers];
      const unique = [];
      const seen = new Set();
      for (const u of merged) {
        const n = (u.name || "").trim().toLowerCase();
        const m = (u.mobile || "").trim();
        const fn = (u.family_name || "").trim().toLowerCase();
        const key = `${n}|${m}|${fn}`;
        if (!seen.has(key) && n !== "") {
          seen.add(key);
          unique.push(u);
        }
      }

      saveCachedUsers(cacheKey, unique);
      setVillageUsers(unique);
    }, 200);

    return () => clearTimeout(timer);
  }, [
    values?.village,
    values?.area,
    moiList,
    apiGet,
    getCachedUsers,
    saveCachedUsers,
  ]);

  // Pre-index village users for fast O(1) Tanglish and phonetic searching (memoized)
  const indexedUsers = useMemo(() => {
    return (villageUsers || []).map(indexUserForSearch);
  }, [villageUsers]);

  // Debounced API transliteration for English search queries
  useEffect(() => {
    const raw = (field.value || "").trim();
    if (!raw || !/[a-zA-Z]/.test(raw)) {
      setApiSuggestions([]);
      return;
    }

    // 1. Check local cache first for instant response
    const cached = getCachedTransliteration(raw);
    if (cached && cached.length > 0) {
      setApiSuggestions(cached);
      return;
    }

    // 2. Debounce network transliteration by 150ms
    const timer = setTimeout(async () => {
      const res = await fetchTransliteration(raw);
      if (res && res.length > 0) {
        setApiSuggestions(res);
      }
    }, 150);

    return () => clearTimeout(timer);
  }, [field.value]);

  // Filter village users: supports instant Tanglish, Phonetic keys, multi-word sentences, and API suggestions
  const query = (field.value || "").trim();
  const filteredUsers = useMemo(() => {
    if (!indexedUsers || indexedUsers.length === 0) return [];
    if (!query) return indexedUsers;

    return indexedUsers.filter((u) =>
      matchUserWithQuery(u, query, apiSuggestions)
    );
  }, [indexedUsers, query, apiSuggestions]);

  // Reset highlight index when filtered list changes
  useEffect(() => {
    setHighlightedIndex(-1);
  }, [filteredUsers]);

  // Scroll active item into view
  useEffect(() => {
    if (highlightedIndex >= 0 && dropdownRef.current) {
      const activeEl = dropdownRef.current.querySelector(
        `[data-user-index="${highlightedIndex}"]`
      );
      if (activeEl) {
        activeEl.scrollIntoView({ block: "nearest" });
      }
    }
  }, [highlightedIndex]);

  // Click outside to dismiss dropdown
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // If the input is currently focused when users finish loading, ensure dropdown is shown
  useEffect(() => {
    const rawVillage = (values?.village || "").trim();
    const inputEl = innerRef?.current || wrapperRef.current?.querySelector("input");
    if (
      rawVillage &&
      inputEl &&
      document.activeElement === inputEl &&
      villageUsers.length > 0
    ) {
      setShowDropdown(true);
    }
  }, [villageUsers, values?.village, innerRef]);

  // Suppress transliteration popups while interacting with dropdown
  useEffect(() => {
    const inputEl = innerRef?.current || wrapperRef.current?.querySelector("input");
    if (!inputEl) return;

    if (showDropdown && filteredUsers.length > 0) {
      inputEl.dataset.suppressTranslate = "true";
      inputEl.classList.add("suppress-translate");
      document.dispatchEvent(new CustomEvent("dismiss-tamil-suggestions"));
    } else {
      const wasSuppressed = inputEl.dataset.suppressTranslate === "true";
      inputEl.dataset.suppressTranslate = "false";
      inputEl.classList.remove("suppress-translate");
      if (wasSuppressed && /[a-zA-Z]/.test(inputEl.value || "")) {
        inputEl.dispatchEvent(
          new CustomEvent("request-tamil-suggestion", { bubbles: true })
        );
      }
    }
  }, [showDropdown, filteredUsers.length, innerRef]);

  // Handle selecting a user
  const handleSelectUser = useCallback(
    (user) => {
      let finalName = user.name || "";
      let finalInitial = user.initial || "";

      if (typeof parseFn === "function") {
        const parsed = parseFn(user.name);
        if (parsed.name) finalName = parsed.name;
        if (parsed.initial) finalInitial = parsed.initial;
      }

      helpers.setValue(finalName);
      if (finalInitial) {
        setFieldValue("initial", finalInitial);
      }
      if (user.family_name) {
        setFieldValue("familyName", user.family_name);
      }
      if (user.mobile) {
        setFieldValue("mobile", user.mobile);
      }
      if (user.occupation) {
        setFieldValue("occupation", user.occupation);
      }
      if (user.village && !values?.village) {
        setFieldValue("village", user.village);
      }
      if (user.area && !values?.area) {
        setFieldValue("area", user.area);
      }
      if (user.city && !values?.city) {
        setFieldValue("city", user.city);
      }
      if (user.state && !values?.state) {
        setFieldValue("state", user.state);
      }

      setShowDropdown(false);
      setHighlightedIndex(-1);

      document.dispatchEvent(new CustomEvent("dismiss-tamil-suggestions"));
      const inputEl = innerRef?.current || wrapperRef.current?.querySelector("input");
      if (inputEl) {
        inputEl.dataset.suppressTranslate = "false";
        inputEl.classList.remove("suppress-translate");
      }

      // Advance focus forward to occupationRef
      setTimeout(() => {
        occupationRef?.current?.focus();
      }, 50);
    },
    [
      helpers,
      setFieldValue,
      parseFn,
      values?.village,
      values?.area,
      values?.city,
      values?.state,
      innerRef,
      occupationRef,
    ]
  );

  // Key navigation
  const handleKeyDown = (e) => {
    const hasDropdown = showDropdown && filteredUsers.length > 0;

    if (hasDropdown) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev + 1 >= filteredUsers.length ? 0 : prev + 1
        );
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev <= 0 ? filteredUsers.length - 1 : prev - 1
        );
        return;
      }
      if (e.key === "Enter") {
        if (highlightedIndex >= 0 && filteredUsers[highlightedIndex]) {
          e.preventDefault();
          e.stopPropagation();
          handleSelectUser(filteredUsers[highlightedIndex]);
          return;
        }
        // No item highlighted: close dropdown and advance focus to nextRef
        setShowDropdown(false);
        if (nextRef?.current) {
          e.preventDefault();
          e.stopPropagation();
          nextRef.current.focus();
          return;
        }
      }
      if (e.key === "Escape") {
        e.preventDefault();
        setShowDropdown(false);
        return;
      }
      if (e.key === "Tab") {
        setShowDropdown(false);
        return;
      }
    } else {
      // Normal Enter handling when dropdown is closed or empty
      if (e.key === "Enter") {
        if (nextRef?.current) {
          e.preventDefault();
          e.stopPropagation();
          nextRef.current.focus();
        }
      }
    }
  };

  const isVisible = showDropdown && filteredUsers.length > 0;

  return (
    <div
      ref={wrapperRef}
      className="relative w-full village-user-name-combobox"
      onFocusCapture={() => {
        const rawVillage = (values?.village || "").trim();
        if (rawVillage && villageUsers.length > 0) {
          setShowDropdown(true);
        }
      }}
      onInputCapture={() => {
        const rawVillage = (values?.village || "").trim();
        if (rawVillage && villageUsers.length > 0) {
          setShowDropdown(true);
        }
      }}
    >
      <input
        {...field}
        {...props}
        ref={innerRef}
        id={id}
        name={name}
        type="text"
        placeholder={placeholder}
        autoComplete="off"
        className={className}
        onKeyDown={handleKeyDown}
        aria-autocomplete="list"
        aria-expanded={isVisible}
        role="combobox"
        aria-controls={isVisible ? "village-users-listbox" : undefined}
      />

      {isVisible && (
        <div
          ref={dropdownRef}
          role="listbox"
          id="village-users-listbox"
          className="absolute z-50 left-0 top-full mt-1.5 w-full min-w-[280px] sm:min-w-[340px] max-h-72 bg-white/95 backdrop-blur-md rounded-2xl border-2 border-indigo-200 shadow-2xl overflow-hidden flex flex-col animation-in fade-in slide-in-from-top-1 duration-150"
        >
          {/* Header Bar */}
          <div className="bg-gradient-to-r from-indigo-50 to-indigo-100/70 px-3 py-2 border-b border-indigo-100 flex items-center justify-between text-xs">
            <div className="flex items-center gap-1.5 text-indigo-900 font-bold truncate">
              <span className="text-sm">👥</span>
              <span className="truncate">{values?.village || "All Locations"}</span>
              {values.area && (
                <>
                  <span className="text-indigo-400 font-semibold">/</span>
                  <span className="truncate text-indigo-700 bg-indigo-200/60 px-1.5 py-0.5 rounded text-[11px] font-semibold">
                    📍 {values.area}
                  </span>
                </>
              )}
              {loading ? (
                <span className="inline-block w-3 h-3 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin shrink-0" />
              ) : (
                <span className="bg-indigo-600 text-white text-[10px] px-1.5 py-0.5 rounded-full font-extrabold shrink-0">
                  {filteredUsers.length}
                </span>
              )}
            </div>
            <span className="text-[10px] text-indigo-500 font-medium hidden sm:inline shrink-0">
              ↑↓ navigate • ↵ select • esc close
            </span>
          </div>

          {/* User List */}
          <div className="overflow-y-auto max-h-60 divide-y divide-gray-100 py-1">
            {filteredUsers.map((user, idx) => {
              const isHighlighted = idx === highlightedIndex;
              let displayName = user.name || "";
              let displayInitial = user.initial || "";

              if (typeof parseFn === "function") {
                const parsed = parseFn(user.name);
                if (parsed.name) displayName = parsed.name;
                if (parsed.initial) displayInitial = parsed.initial;
              }

              return (
                <div
                  key={`${user.name}-${user.mobile || ""}-${idx}`}
                  data-user-index={idx}
                  role="option"
                  aria-selected={isHighlighted}
                  onMouseEnter={() => setHighlightedIndex(idx)}
                  onMouseDown={(e) => {
                    e.preventDefault(); // prevent blur before selection
                    handleSelectUser(user);
                  }}
                  className={`px-3 py-2 cursor-pointer transition-all flex flex-col gap-0.5 select-none ${
                    isHighlighted
                      ? "bg-indigo-50 border-l-4 border-indigo-600 pl-2.5 shadow-sm"
                      : "hover:bg-gray-50/80"
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <span className="text-sm font-bold text-gray-900 truncate">
                        {displayName}
                      </span>
                      {displayInitial && (
                        <span className="bg-indigo-100 text-indigo-800 text-[11px] font-black px-1.5 py-0.2 rounded uppercase shrink-0">
                          {displayInitial}
                        </span>
                      )}
                    </div>
                    {user.occupation && (
                      <span className="text-[10px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded truncate max-w-[110px]">
                        {user.occupation}
                      </span>
                    )}
                  </div>

                  {(user.area || user.family_name || user.mobile) && (
                    <div className="flex items-center gap-2 text-xs text-gray-500 font-medium flex-wrap">
                      {user.area && (
                        <span className="text-[10px] text-indigo-700 bg-indigo-50 border border-indigo-200/80 px-1.5 py-0.2 rounded font-semibold shrink-0">
                          📍 {user.area}
                        </span>
                      )}
                      {user.family_name && (
                        <span className="truncate">
                          த/பெ: <span className="text-gray-700">{user.family_name}</span>
                        </span>
                      )}
                      {user.mobile && (
                        <span className="font-mono text-[11px] text-gray-600 shrink-0 ml-auto">
                          📱 {user.mobile}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default VillageUserNameInput;
